import { useEffect, useState } from "react";
import useBlobStorage from "hooks/useBlobStorage";

interface AttachmentWithUrl {
	url: string;
	fileName: string;
	fileExt: string;
	isPdf: boolean;
	blobUrl: string | null;
	fileId?: number;
}

interface UseAttachments {
	attachmentUrls?: string[];
	attachmentsMap?: Map<number, string>;
	containerId: string;
}

const useAttachments = ({ attachmentUrls, attachmentsMap, containerId }: UseAttachments) => {
	const { getContainerBlob } = useBlobStorage();
	const [attachmentsWithUrls, setAttachmentsWithUrls] = useState<AttachmentWithUrl[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const downloadFile = async (url: string, fileId?: number) => {
			const fileName = url.split("/").pop() || "Attachment";
			const fileExt = fileName.split(".").pop()?.toLowerCase() || "";
			const isPdf = fileExt === "pdf";

			try {
				const blobUrl = await getContainerBlob(containerId, url);
				return {
					url,
					fileName,
					fileExt,
					isPdf,
					blobUrl,
					fileId
				};
			} catch (error) {
				console.error("Failed to fetch blob URL for attachment:", error);
				return {
					url,
					fileName,
					fileExt,
					isPdf,
					blobUrl: null,
					fileId
				};
			}
		};
		const fetchBlobUrls = async () => {
			if ((!attachmentUrls || attachmentUrls.length === 0) && (!attachmentsMap || attachmentsMap.size === 0)) {
				setAttachmentsWithUrls([]);
				return;
			}

			setIsLoading(true);
			setError(null);

			try {
				let attachmentPromises: Promise<AttachmentWithUrl>[];

				if (attachmentsMap && attachmentsMap.size > 0) {
					// Process attachmentsMap - key is fileId, value is url
					attachmentPromises = Array.from(attachmentsMap.entries()).map(async ([fileId, url]) =>
						downloadFile(url, fileId)
					);
				} else {
					// Process attachmentUrls - no fileId
					attachmentPromises = (attachmentUrls || []).map(async (url) => downloadFile(url));
				}

				const attachmentsWithBlobUrls = await Promise.all(attachmentPromises);
				setAttachmentsWithUrls(attachmentsWithBlobUrls);
			} catch (err) {
				setError(err instanceof Error ? err.message : "Failed to fetch attachments");
			} finally {
				setIsLoading(false);
			}
		};

		fetchBlobUrls();
	}, [attachmentUrls, attachmentsMap, containerId, getContainerBlob]);

	return { attachmentsWithUrls, isLoading, error };
};

export default useAttachments;
export type { AttachmentWithUrl };
